import { Typography as MuiTypography, Tooltip, TypographyProps } from "@mui/material";
import React, { cloneElement, isValidElement, useEffect, useRef, useState } from "react";

function getStringFromChildren(children: React.ReactNode): string {
  if (typeof children === "string") {
    return children;
  }

  if (Array.isArray(children)) {
    return children.map((child) => getStringFromChildren(child)).join("");
  }

  if (React.isValidElement(children)) {
    return getStringFromChildren(children.props.children);
  }

  return "";
}

const variantMap = {
  main: "main",
  title: "title",
  subtitle: "subtitle",
  body: "body",
  detail: "detail",
  small: "small"
} as const;

const fontWeightMap = {
  thin: 100,
  extralight: 200,
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900
} as const;

const textTransformMap = {
  uppercase: "uppercase",
  lowercase: "lowercase",
  capitalize: "capitalize",
  none: "none"
} as const;

interface CustomTypographyProps extends TypographyProps {
  children?: React.ReactNode;
  /** Typography variant - controls the overall text style */
  variant?: keyof typeof variantMap;
  /** Font weight - controls how bold or light the text appears */
  fontWeight?: keyof typeof fontWeightMap;
  /** Text transformation - controls capitalization */
  textTransform?: keyof typeof textTransformMap;
}

export function Typography(props: CustomTypographyProps) {
  const {
    children,
    variant = "body", // Set variant to body by default
    fontWeight = "normal", // Set font weight to normal by default
    textTransform = "none", // Set text transform to none by default
    ...otherProps
  } = props;

  const tooltipRef = useRef<HTMLElement>(null);
  const textRef = useRef<HTMLElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      const isTextOverflowing = textRef.current ? textRef.current.scrollWidth > textRef.current.clientWidth : false;
      const isTooltipOverflowing = tooltipRef.current
        ? tooltipRef.current.scrollWidth > tooltipRef.current.clientWidth
        : false;

      setIsTextTruncated(isTextOverflowing || isTooltipOverflowing);
    };

    checkTruncation();
    // Add resize listener to check truncation on window resize
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [children]); // Re-run when content changes
  // Clone the child element to pass the ref
  const childWithRef = isValidElement(children)
    ? cloneElement(children as React.ReactElement, { ref: textRef })
    : children;
  const title = getStringFromChildren(children);

  return (
    <Tooltip
      title={title}
      ref={tooltipRef}
      disableHoverListener={!isTextTruncated}
      sx={{
        // zIndex value to be over modals so the tooltip shows up properly
        zIndex: 1_000,
      }}
    >
      <MuiTypography
        {...otherProps}
        variant={variant}
        sx={{
          fontWeight: fontWeightMap[fontWeight],
          textTransform: textTransformMap[textTransform],
          ...otherProps.sx
        }}
      >
        {childWithRef}
      </MuiTypography>
    </Tooltip>
  );
}
